import React, { useState } from "react";
import { CircularProgress, Box, Typography, TextField, Checkbox, FormControlLabel, Alert, Button } from "@mui/material";
import ConfirmDialog from "../../../components/ConfirmDialog/ConfirmDialog";
import Toast from "../../../components/support/Toast";
import { AnonymizePatientAPI } from "../../../api/API";
import usePatientExport from "./usePatientExport";

/**
 * Diálogo "Anonimizar paciente" (LGPD) da ficha.
 * Aberto pelo painel "Privacidade e dados" da aba Dados pessoais
 * (PatientPrivacyPanel), que só aparece para o dono da clínica. Fica sempre
 * montado para o Toast de sucesso sobreviver à recarga do paciente (quando o
 * botão dá lugar ao selo "Anonimizado").
 * Especificação: docs/security/handoff-l05-l10-anonimizacao-e-eliminacao.md
 */
function PatientAnonymizeDialog({ patientId, patientName, open, onClose, onDone }) {
    const [loading, setLoading] = useState(false);
    const [typedName, setTypedName] = useState("");
    const [checked, setChecked] = useState(false);
    const [toast, setToast] = useState({ open: false, message: "", severity: "error" });
    const { exporting, exported, exportNow } = usePatientExport(patientId);

    const showToast = (message, severity = "error") => {
        setToast({ open: true, message, severity });
    };

    const handleExportNow = async () => {
        const result = await exportNow();
        if (!result.success) {
            showToast(result.error);
        }
    };

    const resetForm = () => {
        setTypedName("");
        setChecked(false);
    };

    const handleCancel = () => {
        if (loading || exporting) return;
        onClose();
        resetForm();
    };

    const canConfirm = !loading && !exporting && typedName.trim() === (patientName || "").trim() && checked;

    const OnConfirmHandler = () => {
        if (!canConfirm) return;
        setLoading(true);

        AnonymizePatientAPI(
            patientId,
            (response) => {
                setLoading(false);
                if (!response || response.success === false) {
                    showToast(response?.message || "Não foi possível anonimizar este paciente.");
                    return;
                }
                onClose();
                resetForm();
                showToast("Paciente anonimizado.", "success");
                if (onDone) onDone();
            },
            (error) => {
                setLoading(false);
                showToast(error?.message || "Não foi possível anonimizar este paciente.");
            }
        );
    };

    return (
        <>
            <ConfirmDialog
                isOpen={open}
                variant="danger"
                title="Anonimizar paciente"
                confirmLabel="Anonimizar"
                loading={loading}
                confirmDisabled={!canConfirm}
                onConfirm={OnConfirmHandler}
                onCancel={handleCancel}
            >
                <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                    <Typography variant="body2">
                        Isto remove os dados pessoais deste paciente: nome, CPF, data de nascimento, telefone,
                        e-mail, endereço e responsável financeiro.
                    </Typography>

                    <Typography variant="body2">
                        Permanecem, ligados a <strong>{`"Paciente anonimizado #${patientId}"`}</strong>: histórico
                        clínico, anamneses, fotos, anexos, documentos, agendamentos passados, financeiro e notas
                        fiscais.
                    </Typography>

                    <Typography variant="body2">
                        Os agendamentos futuros serão cancelados, mensagens automáticas pendentes serão descartadas,
                        o paciente sairá da lista de espera, o vínculo com o lead de origem será desfeito e o
                        paciente ficará inativo. Esta ação é irreversível e fica registrada na auditoria da clínica.
                    </Typography>

                    {exported ? (
                        <Alert severity="success">Exportação concluída. Você pode prosseguir.</Alert>
                    ) : (
                        <Alert
                            severity="info"
                            action={
                                <Button
                                    color="inherit"
                                    size="small"
                                    onClick={handleExportNow}
                                    disabled={exporting}
                                    startIcon={exporting ? <CircularProgress size={14} color="inherit" /> : null}
                                >
                                    Exportar dados agora
                                </Button>
                            }
                        >
                            Recomendamos exportar os dados antes.
                        </Alert>
                    )}

                    <TextField
                        size="small"
                        label="Digite o nome exato do paciente para confirmar"
                        value={typedName}
                        onChange={(e) => setTypedName(e.target.value)}
                        helperText={`Nome esperado: "${patientName}"`}
                        fullWidth
                        disabled={loading}
                    />

                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={checked}
                                onChange={(e) => setChecked(e.target.checked)}
                                disabled={loading}
                            />
                        }
                        label="Entendo que esta ação é irreversível"
                    />
                </Box>
            </ConfirmDialog>

            <Toast
                open={toast.open}
                message={toast.message}
                severity={toast.severity}
                onClose={() => setToast((current) => ({ ...current, open: false }))}
            />
        </>
    );
}

export default PatientAnonymizeDialog;
